import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import * as pdfjsLib from "pdfjs-dist";

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

export const downloadApplication = async (url, token) => {
    try {
        const response = await fetch(url, {
            method: "GET",
            headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) throw new Error("Failed to fetch application data");

        const data = await response.json();
        if (!data.success || !data.data) throw new Error("Invalid application data structure");

        const studentData = data.data || {};
        const applicationNo = studentData.application_no || "N/A";

        // Helper functions
        const safelyGet = (val, defaultVal = "-") => val ? String(val).trim() : defaultVal;
        const getNested = (obj, key) => obj && obj[key] ? String(obj[key]).trim() : "-";

        // Extract complex fields safely
        const fullName = `${studentData.first_name || ''} ${studentData.last_name || ''}`.trim() || "-";
        const className = getNested(studentData.class, 'name');
        const gender = getNested(studentData.gender, 'gender');
        const medium = getNested(studentData.medium, 'media');
        const country = getNested(studentData.country, 'country') || studentData.nationality || "-";

        // Fix for Mother Tongue: Fallback to checking the nested language object, or the primitive string
        let language = "-";
        if (studentData.language?.language) language = studentData.language.language;
        else if (studentData.mother_tongue) language = studentData.mother_tongue;

        // Fix for Community: Fallback to primitive string if relational object doesn't exist
        let caste = "-";
        if (studentData.casteCategory?.caste_category) caste = studentData.casteCategory.caste_category;
        else if (studentData.caste) caste = studentData.caste;

        const bloodGroup = getNested(studentData.bloodgroup, 'blood_group');
        const board = getNested(studentData.board, 'board_name');

        // Co-curricular & Extra-curricular
        const extraActivities = (studentData.extra_curricular_activities || []).map(a => a.activity_name).join(', ') || "-";
        const coCurricular = studentData.co_curricular_activities?.activity_name || "-";

        // Create PDF
        const pdfDoc = await PDFDocument.create();
        const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
        const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
        const black = rgb(0, 0, 0);
        const blue = rgb(0.1, 0.3, 0.6); // Header color
        const darkGray = rgb(0.2, 0.2, 0.2);
        const lightGray = rgb(0.95, 0.95, 0.95);
        const borderColor = rgb(0.7, 0.7, 0.7);

        let page = pdfDoc.addPage([595, 842]);
        let y = 800;
        const startX = 40;
        const endX = 555;
        const width = endX - startX;

        // --- HEADER ---
        page.drawText("Om Shri Sathya Sai Nathaya Namaha", { x: 200, y: 815, size: 9, font: boldFont, color: black });

        try {
            const logoImageBytes = await fetch("/ssvk-logo.png").then(res => res.arrayBuffer());
            const logoImage = await pdfDoc.embedPng(logoImageBytes);
            page.drawImage(logoImage, { x: 40, y: 730, width: 70, height: 70 });
        } catch (e) { console.warn("Logo failed to load"); }

        page.drawText("SHRI SATHYA SAI HOSTELS (BOYS/GIRLS)", { x: 140, y: 785, size: 14, font: boldFont, color: blue });
        page.drawText("Sathya Sai Seva Giri, Post: Chokkady, Sullia Taluk, D.K. District, Pin: 574212", { x: 130, y: 765, size: 10, font, color: black });
        page.drawText("(Run by Bhagavan Shri Sathya Sai Seva Trust)  |  Mob: 6360099621", { x: 145, y: 750, size: 9, font, color: black });
        page.drawText("Website: www.ssvkchokkady.org", { x: 220, y: 735, size: 10, font: boldFont, color: blue });

        // Photo Box
        page.drawRectangle({ x: 480, y: 705, width: 75, height: 95, borderColor: borderColor, borderWidth: 1 });

        let photoEmbedded = false;
        if (studentData.student_photo_url) {
            try {
                let photoUrl = studentData.student_photo_url;
                if (photoUrl.startsWith('/storage')) {
                    const baseUrl = process.env.REACT_APP_API_BASE_URL ? process.env.REACT_APP_API_BASE_URL.replace('/api', '') : 'http://127.0.0.1:8000';
                    photoUrl = `${baseUrl}${photoUrl}`;
                }
                const photoResponse = await fetch(photoUrl);
                if (photoResponse.ok) {
                    const photoBytes = await photoResponse.arrayBuffer();
                    const contentType = photoResponse.headers.get("content-type");
                    let studentPhoto = contentType?.includes("png") ? await pdfDoc.embedPng(photoBytes) : await pdfDoc.embedJpg(photoBytes);
                    if (studentPhoto) {
                        page.drawImage(studentPhoto, { x: 482, y: 707, width: 71, height: 91 });
                        photoEmbedded = true;
                    }
                }
            } catch (error) { console.warn("Failed to embed photo", error); }
        }

        if (!photoEmbedded) {
            page.drawText("Photo", { x: 505, y: 750, size: 8, font, color: borderColor });
        }

        // Title
        page.drawText("APPLICATION FORM", { x: 240, y: 700, size: 12, font: boldFont, color: black });
        page.drawLine({ start: { x: 240, y: 697 }, end: { x: 365, y: 697 }, thickness: 1, color: black });

        y = 670;

        // --- GRID / TABLE DRAWING HELPERS ---
        const drawSectionHeader = (title) => {
            page.drawRectangle({ x: startX, y: y - 12, width: width, height: 20, color: lightGray, borderColor: borderColor, borderWidth: 1 });
            page.drawText(title, { x: startX + 5, y: y - 7, size: 10, font: boldFont, color: blue });
            y -= 12;
        };

        const wrapText = (text, maxChars) => {
            if (!text) return [''];
            const str = String(text);
            const words = str.split(' ');
            const lines = [];
            let currentLine = '';
            words.forEach(word => {
                if ((currentLine + word).length > maxChars) {
                    if (currentLine) lines.push(currentLine.trim());
                    currentLine = word + ' ';
                } else {
                    currentLine += word + ' ';
                }
            });
            if (currentLine) lines.push(currentLine.trim());
            return lines.length ? lines : [''];
        };

        const drawRow2Col = (k1, v1, k2, v2, v1Font = font, v2Font = font) => {
            const lines1 = wrapText(v1 || '', 32);
            const lines2 = wrapText(v2 || '', 32);
            const maxLines = Math.max(lines1.length, lines2.length);
            const h = Math.max(20, maxLines * 12 + 8);

            // Background & Border
            page.drawRectangle({ x: startX, y: y - h, width: width, height: h, borderColor: borderColor, borderWidth: 1 });
            // Vertical split lines
            const col1W = 110;
            const col3W = 100;
            page.drawLine({ start: { x: startX + col1W, y: y }, end: { x: startX + col1W, y: y - h }, thickness: 1, color: borderColor }); // Split k1/v1
            page.drawLine({ start: { x: startX + (width / 2), y: y }, end: { x: startX + (width / 2), y: y - h }, thickness: 1, color: borderColor }); // Split v1/k2
            page.drawLine({ start: { x: startX + (width / 2) + col3W, y: y }, end: { x: startX + (width / 2) + col3W, y: y - h }, thickness: 1, color: borderColor }); // Split k2/v2

            // Text
            page.drawText(k1, { x: startX + 5, y: y - 14, size: 9, font: boldFont, color: darkGray });
            lines1.forEach((line, i) => {
                page.drawText(line, { x: startX + col1W + 5, y: y - 14 - (i * 12), size: 9, font: v1Font, color: black });
            });

            page.drawText(k2, { x: startX + (width / 2) + 5, y: y - 14, size: 9, font: boldFont, color: darkGray });
            lines2.forEach((line, i) => {
                page.drawText(line, { x: startX + (width / 2) + col3W + 5, y: y - 14 - (i * 12), size: 9, font: v2Font, color: black });
            });

            y -= h;
        };

        const drawRow1Col = (k1, v1, v1Font = font) => {
            const lines = wrapText(v1 || '', 85);
            const h = Math.max(20, lines.length * 12 + 8);

            page.drawRectangle({ x: startX, y: y - h, width: width, height: h, borderColor: borderColor, borderWidth: 1 });
            const col1W = 110;
            page.drawLine({ start: { x: startX + col1W, y: y }, end: { x: startX + col1W, y: y - h }, thickness: 1, color: borderColor });

            page.drawText(k1, { x: startX + 5, y: y - 14, size: 9, font: boldFont, color: darkGray });
            lines.forEach((line, i) => {
                page.drawText(line, { x: startX + col1W + 5, y: y - 14 - (i * 12), size: 9, font: v1Font, color: black });
            });
            y -= h;
        };

        const checkPageBreak = (neededSpace) => {
            if (y - neededSpace < 40) {
                page = pdfDoc.addPage([595, 842]);
                y = 800;
            }
        };

        // --- SECTION 1: Personal Details ---
        drawSectionHeader("1. Personal Information");
        drawRow2Col("Application No", applicationNo, "Class Applied For", className, font, boldFont);
        drawRow1Col("Full Name", fullName.toUpperCase(), boldFont);

        const formatDate = (dateStr) => {
            if (!dateStr) return "";
            return new Date(dateStr).toLocaleDateString("en-GB");
        };

        drawRow2Col(
            "Date of Birth",
            formatDate(safelyGet(studentData.dob)),
            "Gender",
            gender
        );
        drawRow2Col("Place of Birth", safelyGet(studentData.birth_place), "Nationality", country);
        drawRow2Col("Religion", safelyGet(studentData.religion), "Community", caste);
        drawRow2Col("Mother Tongue", language, "Blood Group", bloodGroup);
        drawRow1Col("Aadhar Number", safelyGet(studentData.candidate_adhaar_no));
        drawRow2Col("SATS Number", safelyGet(studentData.candidate_sats_no), "PEN", safelyGet(studentData.candidate_pen_no));

        y -= 10;
        checkPageBreak(150);

        // --- SECTION 2: Contact Information ---
        drawSectionHeader("2. Contact & Address");
        drawRow1Col("Address Line 1", safelyGet(studentData.address1));
        drawRow1Col("Address Line 2", safelyGet(studentData.address2));
        drawRow2Col("City / Village", safelyGet(studentData.city), "Taluk", safelyGet(studentData.taluk));
        drawRow2Col("District", safelyGet(studentData.district), "State", safelyGet(studentData.state));
        drawRow2Col("Pincode", safelyGet(studentData.zip_code), "Email ID", safelyGet(studentData.email || studentData.student_email));
        drawRow2Col("Mobile No", safelyGet(studentData.mobile), "WhatsApp No", safelyGet(studentData.whatsapp_no));

        y -= 10;
        checkPageBreak(150);

        // --- SECTION 3: Parent Details ---
        drawSectionHeader("3. Parent Details");
        drawRow2Col("Father's Name", safelyGet(studentData.father_name), "Occupation", safelyGet(studentData.father_occupation));
        drawRow2Col("Father's Aadhar", safelyGet(studentData.father_adhaar_no), "Annual Income", safelyGet(studentData.parent_annual_income));
        drawRow2Col("Mother's Name", safelyGet(studentData.mother_name), "Occupation", safelyGet(studentData.mother_occupation));
        drawRow2Col("Mother's Aadhar", safelyGet(studentData.mother_adhaar_no), "Phone/Mobile", safelyGet(studentData.mobile));

        y -= 10;
        checkPageBreak(120);

        // --- SECTION 4: Previous School ---
        drawSectionHeader("4. Previous Academic Information");
        drawRow1Col("School Name", safelyGet(studentData.prev_school_name));
        drawRow1Col("School Address", safelyGet(studentData.prev_school_address));
        drawRow2Col("Pincode", safelyGet(studentData.prev_school_pincode), "Affiliated Board", board);
        drawRow1Col("Medium of Instruction", medium);

        y -= 10;
        checkPageBreak(200);

        // --- SECTION 5: Previous Exam Marks ---
        let marksData = {};
        if (studentData.last_annual_exam_marks) {
            try {
                marksData = typeof studentData.last_annual_exam_marks === 'string'
                    ? JSON.parse(studentData.last_annual_exam_marks)
                    : studentData.last_annual_exam_marks;
            } catch { } // Error silently, fallback to empty object
        }

        drawSectionHeader("5. Last Annual Examination Results" + (marksData.marksofClass ? ` (Class: ${marksData.marksofClass})` : ""));
        const mCols = [30, 200, 200]; // No, Subject, Marks

        const drawGridRow = (cols, values, isHeader = false) => {
            const h = 20;
            page.drawRectangle({ x: startX, y: y - h, width: cols.reduce((a, b) => a + b, 0), height: h, borderColor: borderColor, borderWidth: 1, color: isHeader ? lightGray : undefined });
            let currX = startX;
            for (let i = 0; i < cols.length; i++) {
                if (i > 0) page.drawLine({ start: { x: currX, y: y }, end: { x: currX, y: y - h }, thickness: 1, color: borderColor });
                page.drawText(values[i], { x: currX + 5, y: y - 14, size: 9, font: isHeader ? boldFont : font, color: black });
                currX += cols[i];
            }
            y -= h;
        };

        drawGridRow(mCols, ["No.", "Subjects", "Marks / Grades"], true);
        const subjects = [
            { label: "Kannada", key: "Kannada" },
            { label: "English", key: "English" },
            { label: `Language (${studentData.marks_language_text || (language !== "-" ? language : "Specify")})`, key: "LanguageMarks" },
            { label: "Mathematics", key: "Maths" },
            { label: "Science", key: "Science" },
            { label: "Social", key: "Social" }
        ];

        subjects.forEach((subj, i) => {
            let markVal = marksData[subj.key] || "-";
            drawGridRow(mCols, [String(i + 1), subj.label, String(markVal)]);
        });
        drawGridRow([230, 200], ["Total:", String(marksData.Total || "-")], true);
        drawGridRow([230, 200], ["Percentage / CGPA:", String(marksData.PercentageCGPA || "-")], true);

        y -= 10;
        checkPageBreak(250);

        // --- SECTION 6: Siblings in SSVK ---
        drawSectionHeader("6. Brothers/Sisters studied/studying in SSVK Chokkady");
        if (studentData.siblings_in_ssv === 'yes') {
            // Check if older JSON structure exists, try to extract names, else use as string
            let sibNames = safelyGet(studentData.siblings_data);
            try {
                if (sibNames && sibNames.startsWith('[')) {
                    const parsed = JSON.parse(sibNames);
                    sibNames = parsed.map(s => s.name).join(", ");
                }
            } catch (e) { }
            drawRow1Col("Details", sibNames || "Yes");
        } else {
            drawRow1Col("Status", "No siblings in SSVK");
        }

        y -= 10;
        checkPageBreak(200);

        // --- SECTION 7: Other Activities & Details ---
        drawSectionHeader("7. Other Activities & Details");
        drawRow2Col("Bala Vikas Student?", safelyGet(studentData.been_to_sbv), "Health Problems?", studentData.health_file_url ? "Yes" : "No");
        drawRow1Col("Extra-curricular Activities", extraActivities);
        drawRow1Col("Co-curricular Activity", coCurricular);
        drawRow1Col("Achievements / Awards", safelyGet(studentData.achievements));
        drawRow1Col("Aim and Objective in Life", safelyGet(studentData.candidate_aim));

        y -= 25;
        checkPageBreak(180);

        // --- DECLARATION & SIGNATURES ---
        page.drawText("DECLARATION BY THE APPLICANT", { x: 200, y, size: 10, font: boldFont, color: black });
        page.drawLine({ start: { x: 200, y: y - 2 }, end: { x: 395, y: y - 2 }, thickness: 1, color: black });
        y -= 20;

        const decText = "I do hereby declare that all the particulars given above by me are true and correct to the best of my knowledge. I undertake to abide by \n" +
            "the rules and regulations of the institution with respect to the admission procedures, academic, sports and extra curricular activities,\n" +
            "code of conduct, discipline and moral education as given in the prospectus. In the event of any act of indiscipline on my part the\n" +
            "decision of the concerned authorities shall be final and binding on me. I endorse the above statements.";
        page.drawText(decText, { x: startX, y, size: 8, font, color: darkGray, lineHeight: 14 });

        y -= 90;

        const fetchImageWithFallback = async (url) => {
            if (!url) return null;
            let finalUrl = url;
            if (finalUrl.startsWith('/storage')) {
                const baseUrl = process.env.REACT_APP_API_BASE_URL ? process.env.REACT_APP_API_BASE_URL.replace('/api', '') : 'http://127.0.0.1:8000';
                finalUrl = `${baseUrl}${finalUrl}`;
            }
            try {
                const response = await fetch(finalUrl);
                if (response.ok) {
                    const bytes = await response.arrayBuffer();
                    const contentType = response.headers.get("content-type") || "";
                    return { bytes, contentType };
                }
                console.warn(`Fetch returned ${response.status} for ${finalUrl}`);
            } catch (e) {
                console.warn(`Failed to fetch: ${finalUrl}`, e);
            }
            return null;
        };

        const convertPdfToImage = async (pdfBytes) => {
            try {
                const loadingTask = pdfjsLib.getDocument({ data: pdfBytes });
                const pdfDocument = await loadingTask.promise;
                const pdfPage = await pdfDocument.getPage(1);

                const scale = 2.0;
                const viewport = pdfPage.getViewport({ scale });

                const canvas = document.createElement("canvas");
                const context = canvas.getContext("2d");
                canvas.width = viewport.width;
                canvas.height = viewport.height;

                const renderContext = {
                    canvasContext: context,
                    viewport: viewport,
                };

                await pdfPage.render(renderContext).promise;

                const imgDataUrl = canvas.toDataURL("image/jpeg", 0.7);
                const base64 = imgDataUrl.split(",")[1];

                const binaryString = window.atob(base64);
                const len = binaryString.length;
                const bytes = new Uint8Array(len);
                for (let i = 0; i < len; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }
                return bytes;
            } catch (e) {
                console.error("PDF to Image conversion failed", e);
                return null;
            }
        };

        const embedSig = async (url, xPos, yPos) => {
            const sigData = await fetchImageWithFallback(url);
            if (sigData && sigData.bytes) {
                try {
                    const cType = sigData.contentType.toLowerCase();
                    const urlLower = url.toLowerCase();

                    if (cType.includes("pdf") || urlLower.endsWith(".pdf")) {
                        // Handle PDF signature uploads
                        const imageBytes = await convertPdfToImage(sigData.bytes);
                        if (imageBytes) {
                            const sigImage = await pdfDoc.embedJpg(imageBytes);
                            page.drawImage(sigImage, { x: xPos, y: yPos, width: 100, height: 40 });
                        } else {
                            const [sigPage] = await pdfDoc.embedPdf(sigData.bytes);
                            if (sigPage) {
                                page.drawPage(sigPage, { x: xPos, y: yPos, width: 100, height: 40 });
                            }
                        }
                    } else {
                        // Handle Image signature uploads
                        let sigImage;
                        if (cType.includes("png") || urlLower.endsWith(".png")) {
                            sigImage = await pdfDoc.embedPng(sigData.bytes);
                        } else if (cType.includes("jpeg") || cType.includes("jpg") || urlLower.endsWith(".jpg") || urlLower.endsWith(".jpeg")) {
                            sigImage = await pdfDoc.embedJpg(sigData.bytes);
                        } else {
                            // Fallback attempt
                            try { sigImage = await pdfDoc.embedPng(sigData.bytes); }
                            catch { sigImage = await pdfDoc.embedJpg(sigData.bytes); }
                        }
                        if (sigImage) {
                            page.drawImage(sigImage, { x: xPos, y: yPos, width: 100, height: 40 });
                        }
                    }
                } catch (e) {
                    console.warn(`Failed to embed signature from ${url}`, e);
                }
            }
        };

        // HORIZONTAL SIGNATURE BLOCK
        y -= 40; // Move down for spacing
        if (y < 60) checkPageBreak(100);

        const col1X = startX;
        const col2X = startX + 175;
        const col3X = startX + 350;
        const sigWidth = 140;

        // Embed signatures horizontally
        await embedSig(studentData.student_signature_url, col1X, y + 15);
        await embedSig(studentData.father_signature_url, col2X, y + 15);
        await embedSig(studentData.mother_signature_url, col3X, y + 15);

        // Draw signature lines
        page.drawLine({ start: { x: col1X, y }, end: { x: col1X + sigWidth, y }, thickness: 1, color: black, dashArray: [2, 2] });
        page.drawLine({ start: { x: col2X, y }, end: { x: col2X + sigWidth, y }, thickness: 1, color: black, dashArray: [2, 2] });
        page.drawLine({ start: { x: col3X, y }, end: { x: col3X + sigWidth, y }, thickness: 1, color: black, dashArray: [2, 2] });

        // Draw Labels
        page.drawText("Applicant's Signature", { x: col1X + 10, y: y - 15, size: 10, font: boldFont, color: black });
        page.drawText("Father's Signature", { x: col2X + 10, y: y - 15, size: 10, font: boldFont, color: black });
        page.drawText("Mother's Signature", { x: col3X + 10, y: y - 15, size: 10, font: boldFont, color: black });

        // Save PDF
        const pdfBytes = await pdfDoc.save();
        const blob = new Blob([pdfBytes], { type: "application/pdf" });

        if (!blob.size) throw new Error("Failed to generate PDF");

        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `Application_${applicationNo}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

    } catch (error) {
        console.error("Error generating application PDF:", error);
        alert("Failed to download application. " + error.message);
    }
};
